import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Button } from '@shared/components/ui/button';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { faEye } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getMinimumFeeInvoiceLabel, MinimumFee, MinimumFeeStatus } from '../models/MinimumFee';

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatMonthYear = (year: number | null | undefined, month: number | null | undefined): string => {
  if (!year || !month) return '-';
  return `${String(month).padStart(2, '0')}/${year}`;
};

const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<any>[] =>
  agGridColumns
    .filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    })
    .map((column) => ({
      id: column.field || column.headerName || 'unknown',
      accessorKey: column.field || column.headerName || 'unknown',
      header: column.headerName || 'Columna',
    }));

const statusClassName: Record<MinimumFeeStatus, string> = {
  pending: 'bg-bg-warning text-warning',
  not_required: 'bg-muted text-muted-foreground',
  invoiced: 'bg-bg-success text-success',
};

interface UseMinimumFeeColumnsProps {
  selectedColumns: string[];
  onViewInvoice: (invoiceId: string) => void;
}

export const useMinimumFeeColumns = ({ selectedColumns, onViewInvoice }: UseMinimumFeeColumnsProps) => {
  const { t } = useTranslation('minimumFeesModule');
  const currency = useCurrency();

  const formatMoney = useMemo(
    () =>
      (value: unknown): string => {
        const amount = Number(value ?? 0);
        const formatted = new Intl.NumberFormat('es-ES', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number.isFinite(amount) ? amount : 0);

        return `${formatted} ${currency}`;
      },
    [currency],
  );

  const allColumnDefs: ColDef[] = useMemo(() => {
    const moneyCell =
      (field: keyof MinimumFee, className = 'text-sm') =>
      (params: any) => <span className={className}>{formatMoney(params.data?.[field])}</span>;

    const baseColumns: CustomColDef[] = [
      {
        field: 'team_name',
        headerName: t('table.team', 'Cliente'),
        minWidth: 220,
        valueGetter: (params) => params.data?.team_name ?? '-',
      },
      {
        field: 'period',
        headerName: t('table.period', 'Año y mes'),
        minWidth: 120,
        valueGetter: (params) => formatMonthYear(params.data?.year, params.data?.month),
      },
      {
        field: 'minimum_fee',
        headerName: t('table.minimumFee', 'Cuota mínima'),
        minWidth: 150,
        valueGetter: (params) => params.data?.minimum_fee ?? 0,
        cellRenderer: moneyCell('minimum_fee'),
      },
      {
        field: 'actual_billed_amount',
        headerName: t('table.actualBilledAmount', 'Facturación real'),
        minWidth: 170,
        valueGetter: (params) => params.data?.actual_billed_amount ?? 0,
        cellRenderer: moneyCell('actual_billed_amount'),
      },
      {
        field: 'storage_amount',
        headerName: t('table.storageAmount', 'Almacenaje'),
        minWidth: 140,
        valueGetter: (params) => params.data?.storage_amount ?? 0,
        cellRenderer: moneyCell('storage_amount'),
      },
      {
        field: 'total_computed_amount',
        headerName: t('table.totalComputedAmount', 'Total computado'),
        minWidth: 170,
        valueGetter: (params) => params.data?.total_computed_amount ?? 0,
        cellRenderer: moneyCell('total_computed_amount'),
      },
      {
        field: 'difference_amount',
        headerName: t('table.differenceAmount', 'Diferencia a facturar'),
        minWidth: 190,
        valueGetter: (params) => params.data?.difference_amount ?? 0,
        cellRenderer: moneyCell('difference_amount', 'text-sm font-medium'),
      },
      {
        field: 'status',
        headerName: t('table.status', 'Estado'),
        minWidth: 140,
        valueGetter: (params) => params.data?.status ?? '',
        cellRenderer: (params: any) => {
          const status = params.data?.status as MinimumFeeStatus | undefined;
          if (!status) return <span className="text-sm">-</span>;

          return (
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusClassName[status]}`}>
              {t(`status.${status}`, status)}
            </span>
          );
        },
      },
      {
        field: 'invoice_id',
        headerName: t('table.invoice', 'Factura'),
        minWidth: 180,
        valueGetter: (params) => getMinimumFeeInvoiceLabel(params.data),
        cellRenderer: (params: any) => <span className="text-sm">{getMinimumFeeInvoiceLabel(params.data)}</span>,
      },
      {
        field: 'created_at',
        headerName: t('table.createdAt', 'Fecha creación'),
        minWidth: 180,
        valueGetter: (params) => params.data?.created_at ?? '',
        cellRenderer: (params: any) => <span className="text-sm">{formatDateTime(params.data?.created_at)}</span>,
      },
    ];

    // Columna de acciones fijada a la derecha (mismo patrón que eligible-shipments / BoxGroupsTable)
    baseColumns.push({
      field: 'actions',
      colId: 'actions',
      headerName: '',
      sortable: false,
      filter: false,
      width: 50,
      maxWidth: 50,
      minWidth: 50,
      flex: 0,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right',
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      cellRenderer: (params: any) => {
        const invoiceId = params.data?.invoice_id;
        if (!invoiceId) {
          return <div className="flex justify-center w-full" />;
        }

        return (
          <div className="flex justify-center w-full">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onViewInvoice(String(invoiceId))}
              title={t('actions.viewInvoice', 'Ver factura')}
              aria-label={t('actions.viewInvoice', 'Ver factura')}
              data-testid="view-invoice-button"
            >
              <FontAwesomeIcon icon={faEye} className="w-3.5 h-3.5" />
            </Button>
          </div>
        );
      },
    });

    return baseColumns;
  }, [formatMoney, onViewInvoice, t]);

  const tanStackColumns = useMemo(() => convertToTanStackColumns(allColumnDefs), [allColumnDefs]);

  const filteredColumnDefs = useMemo(() => {
    if (!selectedColumns.length) {
      return allColumnDefs;
    }

    return allColumnDefs.filter((col) => {
      const field = col.field || '';
      if (field === 'actions') {
        return true;
      }
      return selectedColumns.includes(field);
    });
  }, [allColumnDefs, selectedColumns]);

  return {
    allColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
